import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthContext } from '@/contexts/AuthContext';

export interface ApiKey {
  id: string;
  user_id: string;
  product_name: string;
  key_value: string;
  key_prefix: string;
  status: 'active' | 'revoked';
  total_requests: number;
  created_at: string;
  last_used_at: string;
}

function generateKey(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = 'sk_live_';
  for (let i = 0; i < 32; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
}

export function useApiKeys() {
  const { user } = useAuthContext();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchKeys = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from('api_keys')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (fetchError) setError(fetchError.message);
    else setKeys((data ?? []) as ApiKey[]);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const createKey = useCallback(async (productName: string): Promise<ApiKey | null> => {
    if (!user) return null;
    const keyValue = generateKey();
    const keyPrefix = keyValue.slice(0, 20) + '••••••••••••';
    const { data, error: insertError } = await supabase
      .from('api_keys')
      .insert({
        user_id: user.id,
        product_name: productName,
        key_value: keyValue,
        key_prefix: keyPrefix,
        status: 'active',
      })
      .select()
      .maybeSingle();
    if (insertError) { setError(insertError.message); return null; }
    const newKey = data as ApiKey;
    setKeys(prev => [newKey, ...prev]);
    return newKey;
  }, [user]);

  const revokeKey = useCallback(async (id: string) => {
    if (!user) return;
    const { error: updateError } = await supabase
      .from('api_keys')
      .update({ status: 'revoked' })
      .eq('id', id)
      .eq('user_id', user.id);
    if (!updateError) {
      setKeys(prev => prev.map(k => k.id === id ? { ...k, status: 'revoked' as const } : k));
    }
  }, [user]);

  const rotateKey = useCallback(async (id: string, productName: string): Promise<ApiKey | null> => {
    if (!user) return null;
    await supabase.from('api_keys').delete().eq('id', id).eq('user_id', user.id);
    setKeys(prev => prev.filter(k => k.id !== id));
    return createKey(productName);
  }, [user, createKey]);

  return { keys, apiKeys: keys, loading, error, createKey, revokeKey, rotateKey, refetch: fetchKeys };
}
